import { Box, Button, TextField, Link, Typography } from '@mui/material'
import { useState, useEffect } from 'react'
import Modal from './Modal'
import PasswordField from './PasswordField'
import { validatePassword } from '../utils/passwordValidation'
import { api } from '../requests/axios'
import { useNavigate } from 'react-router-dom'

interface SignUpModalProps {
  open: boolean
  onClose: () => void
  onSuccess: (email: string, token: string) => void
  onSwitchToLogin: () => void
}

export default function SignUpModal({ open, onClose, onSuccess, onSwitchToLogin }: SignUpModalProps) {
  const [name, setName] = useState("")
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [passwordConfirm, setPasswordConfirm] = useState("")
  const [passwordError, setPasswordError] = useState<string[]>([])
  const [passwordConfirmError, setPasswordConfirmError] = useState("")
  const navigate = useNavigate()

  const clearForm = () => {
    setName("")
    setEmail("")
    setPassword("")
    setPasswordConfirm("")
    setPasswordError([])
    setPasswordConfirmError("")
  }

  useEffect(() => {
    if (open) {
      clearForm()
    }
  }, [open])

  const isFormValid = (): boolean => {
    return name.trim() !== '' &&
           email.trim() !== '' &&
           password.trim() !== '' &&
           passwordConfirm.trim() !== '' &&
           passwordError.length === 0 &&
           passwordConfirmError === ''
  }

  const handleSignUp = async () => {
    if (!isFormValid()) {
      return
    }
    
    const passwordValidationErrors = validatePassword(password)
    setPasswordError(passwordValidationErrors)

    if (password !== passwordConfirm) {
      setPasswordConfirmError('Passwords do not match')
      return
    } else {
      setPasswordConfirmError('')
    }

    if (passwordValidationErrors.length > 0) {
      return
    }

    let body = { user: { name: name, email: email, password: password }}
    try {
      const response = await api.post((process.env.REACT_APP_API_PROXY_URL || '').concat(`/users.json`), body)
      const data = await response.data

      if(response.status === 201) {
        clearForm()
        // Store name in localStorage if available
        if (name) {
          localStorage.setItem('name', name);
        }
        onSuccess(data.email, data.authentication_token)
        navigate("/color-charts")
      } else {
        alert('Sign up failed. ' + response.data.error)
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.error || 'Sign up failed. Please try again.'
      alert(errorMessage)
    }
  }

  const handleClose = () => {
    clearForm()
    onClose()
  }

  if (!open) return null

  return (
    <Modal title="Sign Up" onClose={handleClose}>
      <Box sx={{padding: 3}}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Create an account to start knitting! 🧶
        </Typography>
        <TextField
          id="signup-name"
          label="Name"
          variant="outlined"
          fullWidth
          required
          sx={{ mb: 2 }}
          value={name}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setName(event.target.value);
          }}
        />
        <TextField
          id="signup-email"
          label="Email"
          type="email"
          variant="outlined"
          fullWidth
          required
          sx={{ mb: 2 }}
          value={email}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setEmail(event.target.value);
          }}
        />
        <PasswordField
          id="signup-password"
          label="Password"
          value={password}
          onChange={(value, errors) => {
            setPassword(value)
            setPasswordError(errors)
            // Check password match when password changes
            if (passwordConfirm && value !== passwordConfirm) {
              setPasswordConfirmError('Passwords do not match')
            } else {
              setPasswordConfirmError('')
            }
          }}
        />
        <TextField
          id="signup-password-confirm"
          label="Confirm Password"
          type="password"
          variant="outlined"
          fullWidth
          required
          sx={{ mb: 2 }}
          error={!!passwordConfirmError}
          helperText={passwordConfirmError}
          value={passwordConfirm}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            const value = event.target.value;
            setPasswordConfirm(value);
            if (value && value !== password) {
              setPasswordConfirmError('Passwords do not match');
            } else {
              setPasswordConfirmError('');
            }
          }}
          onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
            if (event.key === 'Enter') {
              handleSignUp();
            }
          }}
        />
        <Button 
          onClick={handleSignUp}
          disabled={!isFormValid()}
          sx={{ mb: 2 }}
        >
          Sign Up
        </Button>
        <Box sx={{ textAlign: 'center', mt: 2 }}>
          <Typography variant="body2" color="text.secondary" component="span">
            Already have an account?{' '}
            <Link
              component="button"
              variant="body2"
              onClick={() => {
                handleClose()
                onSwitchToLogin()
              }}
              sx={{ cursor: 'pointer', textDecoration: 'none', verticalAlign: 'baseline' }}
            >
              Login
            </Link>
          </Typography>
        </Box>
      </Box>
    </Modal>
  )
}

